import { useCallback } from "react";
import { useUpdateProposalFunds } from "creso-core/hooks";
import { CreateProposalFund } from "creso-core/types";
import { z } from "zod";
import {
  ProposalFormSchema,
  useProposalFormContext,
} from "../providers/proposal-provider";

/**
 * Persists `proposal_funds` (allocations and per-fund remarks) from the shared
 * form. Sidebar step navigation does not save automatically, so any step that
 * relies on the saved fund allocations (e.g. the benchmark backtest) must call
 * this before relying on them being current.
 */
export function useSaveProposalFunds() {
  const form = useProposalFormContext();
  const updateProposalFundsMutation = useUpdateProposalFunds();

  return useCallback(() => {
    return new Promise<void>((res, rej) => {
      form.handleSubmit(
        async (data: z.infer<typeof ProposalFormSchema>) => {
          try {
            if (!data.id)
              throw new Error("Proposal must be saved before saving funds");
            const proposalFunds: CreateProposalFund[] = data.proposal_funds
              .filter(
                (f) =>
                  f.fund_scheme > 0 &&
                  (Number(f.sip_amount ?? 0) > 0 ||
                    Number(f.lumpsum_amount ?? 0) > 0)
              )
              .map((f) => ({
                fund_scheme: f.fund_scheme,
                source: f.source,
                type: f.type,
                sip_amount: f.sip_amount,
                lumpsum_amount: f.lumpsum_amount,
                remarks: f.remarks || "",
                order: f.order as number,
                amc_code: f.amc_code,
                fund_scheme_name: f.fund_scheme_name,
                ...(f.source_folio ? { source_folio: f.source_folio } : {}),
              }));
            await updateProposalFundsMutation.mutateAsync({
              proposalId: data.id,
              proposalFunds,
            });
            res();
          } catch (e) {
            rej(e);
          }
        },
        (errors) => rej(errors)
      )();
    });
  }, [form, updateProposalFundsMutation]);
}
